<template>
    <div height="medium" width="large">
        <div class="container">
            <div class="main">
                <div>
                    <h2 class="heading">
                        {{ $t('modals.patient.medical.heading') }}
                    </h2>
                    <p class="subtitle">{{ $t('modals.patient.medical.subtitle') }}</p>
                </div>
                <div>
                    <button class="btn">
                        <common-icon name="file" size="tiny"></common-icon>
                        {{ $t('modals.patient.medical.title') }}
                    </button>
                </div>
            </div>
            <div class="content-section">
                <h4 class="head">
                    <common-icon name="current" size="medium"></common-icon>
                    {{ $t('modals.patient.medical.head') }}
                </h4>
                <!-- Top 3 cards -->
                <div class="cards">
                    <div class="cardes blue-card">
                        <h3 class="titles">
                            <common-icon name="user" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.blue_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ $t('modals.patient.medical.blue_card.card_subtitle') }}</p>
                        <p class="result">{{ $t('modals.patient.medical.blue_card.card_detail') }}</p>
                    </div>
                    <div class="cardes green-card">
                        <h3 class="titles">
                            <common-icon name="file" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.green_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ $t('modals.patient.medical.green_card.card_subtitle') }}</p>
                        <p class="result">{{ $t('modals.patient.medical.green_card.card_detail') }}</p>
                    </div>
                    <div class="cardes purple-card">
                        <h3 class="titles">
                            <common-icon name="timeline" size="medium"></common-icon>
                            {{ $t('modals.patient.medical.purple_card.card_head') }}
                        </h3>
                        <p class="periodic">{{ $t('modals.patient.medical.purple_card.card_subtitle') }}</p>
                        <p class="result">{{ $t('modals.patient.medical.purple_card.card_detail') }}</p>
                    </div>
                </div>
            </div>
            <!-- Timeline -->
            <div class="content-section">
                <h4 class="head">
                    <common-icon name="timer" size="medium"></common-icon>
                    {{ $t('modals.patient.medical.time_head') }}
                </h4>
                <div class="timeline-wrap">
                    <div class="timeline-inner">
                        <div
                            v-for="(step, idx) in steps"
                            :key="step.id"
                            class="timeline-item"
                            :class="{ active: activeIndex === idx }"
                        >
                            <!-- left marker -->
                            <div class="marker-col">
                                <div class="marker">
                                    <span class="dot" :class="{ completed: idx < activeIndex }"></span>
                                    <span class="line"></span>
                                </div>
                            </div>

                            <!-- card -->
                            <div class="card-col">
                                <article class="card-layout" @click="setActive(idx)">
                                    <!-- header -->
                                    <header class="card-head">
                                        <div class="card-head-left">
                                            <div class="meta-date">
                                                <div class="date">
                                                    <common-icon name="timeline" size="tiny"></common-icon>
                                                    <p>{{ $t(`modals.patient.medical.steps.${idx}.date`) }}</p>
                                                </div>
                                                <div class="time">
                                                    <common-icon name="timer" size="tiny"></common-icon>
                                                    <p>{{ $t(`modals.patient.medical.steps.${idx}.time`) }}</p>
                                                </div>
                                            </div>
                                            <div class="card-title-wrap">
                                                <h3 class="card-title">{{ $t(`modals.patient.medical.steps.${idx}.title`) }}</h3>
                                                <div class="tags">
                                                    <span class="tag">
                                                        <common-icon name="user" size="tiny"></common-icon>
                                                        {{ $t(`modals.patient.medical.steps.${idx}.tags_one`) }}
                                                    </span>
                                                    <span class="btn">
                                                        {{ $t(`modals.patient.medical.steps.${idx}.tags_two`) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="card_grid">
                                            <div class="user-provider">
                                                <common-icon name="user" size="tiny"></common-icon>
                                                {{ $t(`modals.patient.medical.steps.${idx}.provider`) }}
                                            </div>
                                            <div class="location">
                                                <common-icon name="location" size="tiny"></common-icon>
                                                {{ $t(`modals.patient.medical.steps.${idx}.location`) }}
                                            </div>
                                        </div>
                                    </header>

                                    <!-- expanded body -->
                                    <div class="card-body">
                                        <section class="section diagnosis">
                                            <div class="section-title">
                                                {{ $t('modals.patient.medical.steps.diagnosis_name') }}
                                            </div>
                                            <div class="section-content">
                                                {{ $t(`modals.patient.medical.steps.${idx}.diagnosis`) }}
                                            </div>
                                        </section>

                                        <section class="vitals-layout">
                                            <h5>{{ $t('modals.patient.medical.steps.vital_name') }}</h5>
                                            <div class="vitals">
                                                <div class="vital">
                                                    <strong>BP:</strong>
                                                    {{ $t(`modals.patient.medical.steps.${idx}.vitals.bp`) }}
                                                </div>
                                                <div class="vital">
                                                    <strong>HR:</strong>
                                                    {{ $t(`modals.patient.medical.steps.${idx}.vitals.hr`) }}
                                                </div>
                                                <div class="vital">
                                                    <strong>Temp:</strong>
                                                    {{ $t(`modals.patient.medical.steps.${idx}.vitals.temp`) }}
                                                </div>
                                                <div class="vital">
                                                    <strong>Weight:</strong>
                                                    {{ $t(`modals.patient.medical.steps.${idx}.vitals.weight`) }}
                                                </div>
                                            </div>
                                        </section>

                                        <section class="section-notes">
                                            <div class="section-title">{{ $t('modals.patient.medical.steps.note') }}</div>
                                            <div class="section-content">
                                                {{ $t(`modals.patient.medical.steps.${idx}.notes`) }}
                                            </div>
                                        </section>

                                        <div class="actions">
                                            <button class="btn btn-primary">
                                                <common-icon name="file" size="tiny"></common-icon>
                                                {{ $t('modals.patient.medical.steps.view') }}
                                            </button>
                                            <button class="btn btn-primary">
                                                <common-icon name="sms" size="tiny"></common-icon>
                                                {{ $t('modals.patient.medical.steps.contect') }}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="load">
                    <p>{{ $t('modals.patient.medical.steps.load') }}</p>
                    <common-icon name="leftarrow" size="tiny"></common-icon>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    export default {
        mixins: [PageWithDataMixin],
        data() {
            return {
                activeIndex: 0,
                steps: [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }], // sab data $t() se aayega
            }
        },
        methods: {
            setActive(i) {
                this.activeIndex = i
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/patients/_patient';

    .heading {
        font-size: 18px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
    }
    .subtitle {
        color: #666;
        margin-bottom: 16px;
        font-size: 12px;
        font-weight: 400;
    }
    .load {
        display: flex;
        justify-content: center;
        color: #030213;
        align-items: center;
    }
    .load p:hover {
        text-decoration: underline;
    }
    .load p {
        font-size: 14px;
        color: #030213;
        font-weight: 400;
        cursor: pointer;
        margin-right: 12px;
    }
    .content-section {
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #ddd;
        margin-bottom: 20px;
    }
    .user-provider {
        margin-bottom: 6px;
        color: oklch(0.145 0 0);
        font-size: 12px;
        font-weight: 400;
    }
    .location {
        font-size: 12px;
    }
    .cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
        border: none;
    }
    .head {
        font-size: 14px;
        font-weight: 400;
        color: oklch(0.145 0 0);
        padding: 8px 1px;
    }
    h4.head i {
        margin-right: 7px;
    }
    @media (min-width: 768px) {
        .cards {
            grid-template-columns: repeat(3, 1fr);
        }
    }
    .cardes {
        padding: 16px;
        border-radius: 8px;
    }
    .cardes h3 {
        font-weight: 500;
    }
    .blue-card {
        background-color: oklch(0.97 0.014 254.604);
        color: #1e40af;
    }
    .blue-card i {
        color: oklch(0.546 0.245 262.881);
    }
    .green-card {
        background-color: oklch(0.982 0.018 155.826);
        color: #065f46;
    }
    .green-card i {
        color: oklch(0.627 0.194 149.214);
    }
    .purple-card {
        background-color: oklch(0.977 0.014 308.299);
        color: #5b21b6;
    }
    .purple-card i {
        color: oklch(0.558 0.288 302.321);
    }
    .timeline-wrap {
        padding: 20px 0px;
        min-height: 100vh;
        font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
        color: #1f2937;
    }
    .btn svg {
        margin-right: 12px;
    }
    .timeline-item {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        margin-bottom: 20px;
    }
    .marker-col {
        width: 14px;
        display: flex;
        justify-content: center;
        position: relative;
    }
    .marker {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #60a5fa;
        display: inline-block;
        box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.12);
        transition: background 0.2s, transform 0.15s;
    }
    .dot.completed {
        background: #2563eb;
        box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.08);
        transform: scale(1.05);
    }
    .line {
        flex: 1;
        width: 4px;
        background: #60a5fa;
        margin-top: 6px;
        min-height: 420px;
        display: block;
        border-radius: 1px;
    }
    .card-col {
        flex: 1;
    }
    .card-layout {
        border: 1px solid #e6eaf0;
        border-radius: 10px;
        padding: 14px;
        cursor: pointer;
        transition: transform 0.12s, box-shadow 0.12s;
    }
    .card-head-left {
        gap: 12px;
        align-items: flex-start;
    }
    .meta-date {
        min-width: 120px;
        color: #374151;
        display: flex;
        align-items: center;
    }
    .date {
        font-size: 14px;
        font-weight: 500;
        color: oklch(0.145 0 0);
        align-items: center;
    }
    .date p {
        margin-left: 10px;
    }
    .time {
        font-size: 0.875rem;
        color: #6b7280;
        margin-left: 12px;
        font-weight: 400;
        display: flex;
        align-items: center;
    }
    .time p {
        margin-left: 10px;
    }
    .card-title-wrap {
        display: flex;
        flex-direction: column;
    }
    .tags {
        margin-top: 6px;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    .tag {
        display: inline-block;
        padding: 6px 8px;
        background: oklch(0.932 0.032 255.585);
        font-weight: 500;
        color: oklch(0.424 0.199 265.638);
        border-radius: 9px;
        font-size: 10px;
        border: 1px solid rgba(99, 102, 241, 0.08);
    }
    .card_grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-top: 12px;
    }
    .card-body {
        margin-top: 14px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .section {
        background: oklch(0.985 0.002 247.839);
        padding: 10px;
        border-radius: 8px;
    }
    .section-title {
        font-weight: 600;
        margin-bottom: 3px;
        color: #111827;
    }
    .section-content {
        color: #374151;
        line-height: 1.4;
        font-size: 12px;
        font-weight: 400;
    }
    .vitals {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
        align-items: center;
        margin-top: 12px;
    }
    .vitals-layout {
        background: #f0f9ff;
        padding: 12px;
        border-radius: 8px;
        border: 1px solid rgba(99, 102, 241, 0.04);
    }
    .vital {
        font-size: 12px;
        color: #0f172a;
    }
    .btn {
        border: 1px solid #ccc;
        border-radius: 6px;
        padding: 6px 12px;
        cursor: pointer;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 6px;
        margin-right: 12px;
    }
    .actions {
        display: flex;
        gap: 10px;
        margin-top: 6px;
    }

    @media (max-width: 720px) {
        .meta-date {
            min-width: 92px;
        }
        .vitals {
            grid-template-columns: repeat(2, 1fr);
        }
    }
    @media screen and (max-width: 600px) {
        .actions,
        .main,
        .card_grid {
            display: block !important;
            margin-bottom: 10px !important;
        }
        .actions button {
            margin-bottom: 10px !important;
            width: 100% !important;
        }
    }
    .result {
        color: oklch(0.446 0.03 256.802);
        font-size: 12px;
    }
    p.periodic {
        font-size: 12px;
        color: oklch(0.145 0 0);
        font-weight: 400;
    }
</style>
